import { BASE_URL } from '../../constants';
import { secureFetch } from '../../utils/secureFetch';

// Circle = key people (Parent / Guardian / Coach / Trainer / Rep) an athlete
// grants profile access to, each with a permission level:
//   view   "View only"  can look at the profile
//   manage "Manage"     can add details but not remove or change existing ones
//   edit   "Edit"       can make changes to the profile
//
// These thunks return data directly (same pattern as inviteAdmin); the
// Circle page keeps the list in local state, no reducer needed.

// athleteEmail is optional. Omitted = the logged-in athlete's own circle.
// Set it (staff only) to manage another athlete's circle, e.g. an admin or
// intern editing a player's profile.
export const fetchMyCircle = (athleteEmail = null) => async (dispatch, getState) => {
  const qs = athleteEmail ? `?athleteEmail=${encodeURIComponent(athleteEmail)}` : '';
  const data = await secureFetch({
    method: 'GET',
    url: `${BASE_URL}/api/users/circle/${qs}`,
    getState,
  });
  return data?.members || [];
};

// Either an email + relationship, or a rep's Rep ID (repCode), which the
// server resolves to the rep's account with relationship "Rep".
export const addCircleMember = ({ email, name, relationship, permission, athleteEmail = null, repCode = null, shareFinancials = false }) =>
  async (dispatch, getState) => {
    const body = { email, name, relationship, permission, shareFinancials };
    if (repCode) body.repCode = repCode;
    if (athleteEmail) body.athleteEmail = athleteEmail;
    return secureFetch({
      method: 'POST',
      url: `${BASE_URL}/api/users/circle/`,
      body,
      getState,
    });
  };

// Resolve a Rep ID before adding it, so the athlete sees who they link.
export const lookupRepCode = (code) => async (dispatch, getState) =>
  secureFetch({
    method: 'GET',
    url: `${BASE_URL}/api/users/rep/lookup/?code=${encodeURIComponent(code)}`,
    getState,
  });

export const updateCircleMember = (memberId, changes) =>
  async (dispatch, getState) => {
    return secureFetch({
      method: 'PATCH',
      url: `${BASE_URL}/api/users/circle/${memberId}/`,
      body: changes,
      getState,
    });
  };

export const removeCircleMember = (memberId) => async (dispatch, getState) => {
  return secureFetch({
    method: 'DELETE',
    url: `${BASE_URL}/api/users/circle/${memberId}/`,
    getState,
  });
};

export const fetchCircleMemberships = () => async (dispatch, getState) => {
  const data = await secureFetch({
    method: 'GET',
    url: `${BASE_URL}/api/users/circle/memberships/`,
    getState,
  });
  return data?.memberships || [];
};

// Save used by the Circle edit flow (a member editing an athlete's profile).
// Unlike saveAthleteProfile, this rethrows so the wizard can surface the
// backend's permission errors ("Manage" members get 403 on destructive
// changes) instead of silently toasting success.
export const saveAthleteProfileAsCircleMember = (profile) =>
  async (dispatch, getState) => {
    return secureFetch({
      method: 'PUT',
      url: `${BASE_URL}/api/users/athlete/update/`,
      body: profile,
      getState,
    });
  };
